import { pipe } from 'fp-ts/function';
import { array, eq, nonEmptyArray, ord, string } from 'fp-ts';
import { useMemo, useRef } from 'react';
import {
    Customer,
    Reservation,
    ReservationExtent,
    ReservationGroup,
    ReservationResult,
    Resource,
    useRepositoryQuery,
} from '@mews-commander/data-access';
import { compactArray, keyById } from '@mews/fp';
import { UnicodeEntity } from '@mews/enums';
import { TreeOption } from '@mews/b2b-ui';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { CompanionAccommodationExtent } from '@mews-commander/enums';
import { ReservationTimeState, ServiceOrderState } from '@mews-commander/legacy-data-structures';
import { getTagVariantFromReservationTimeState, getTextKeyFromServiceOrderState } from '@mews-commander/utils';
import { LocalizedText } from '@mews/framework';
import { Typography } from '@optimus-web/core';
import { SpacedDash } from '@mews-commander/ui';
import { EnrichedReservation, GroupsAndReservationsDialogDefaultValues } from './types';
import { ReservationLabel } from './ReservationLabel';
import { GROUP_PREFIX, RESERVATION_PREFIX } from './constants';

const RESERVATION_EXTENT = {
    orderItems: false,
    products: false,
    reservationGroups: true,
    availabilityBlocks: false,
    companionships: false,
    accounts: true,
    businessSegments: false,
    generalNotes: false,
    channelManagerNotes: false,
    specialRequestNotes: false,
    vouchers: false,
    partnerCompanies: false,
    rates: false,
    travelAgencies: false,
    origin: false,
    bookingEngines: false,
    purchaseOrderNumbers: false,
} satisfies ReservationExtent;

export const useSearchGroupsAndReservations = (
    searchToken: string,
    defaultValues?: GroupsAndReservationsDialogDefaultValues
): { groupsOptions: TreeOption[]; groupToReservationMap: Record<string, Array<string>> } => {
    const enterpriseId = useCurrentEnterpriseId();

    const { data: groupsSearchData = [], isFetching: isLoadingReservationGroupsSearch } = useRepositoryQuery(
        'reservationGroups',
        'search',
        { searchToken },
        { enabled: !!searchToken }
    );
    const { data: reservationsSearchData = [], isFetching: isLoadingReservationsSearch } = useRepositoryQuery(
        'reservations',
        'search',
        { searchToken },
        { enabled: !!searchToken }
    );

    const { data: resourcesData } = useRepositoryQuery(
        'resources',
        'getAll',
        {
            enterpriseId,
            extent: {
                resourceCategories: false,
                resourceCategoryAssignments: false,
                resourceFeatureAssignments: false,
                resourceFeatures: false,
                resources: true,
                slots: false,
            },
        },
        {
            select: (result) => ({
                resources: keyById(result.resources),
            }),
        }
    );

    const { data: groupSearchReservations, isFetching: isLoadingReservationsAllGroups } = useRepositoryQuery(
        'reservations',
        'getAll',
        {
            groupIds: [...groupsSearchData.map(({ id }) => id), ...(defaultValues?.reservationGroupIds ?? [])],
            extent: RESERVATION_EXTENT,
        },
        {
            enabled: (!!groupsSearchData.length || !!defaultValues?.reservationGroupIds?.length) && !!resourcesData,
        }
    );

    const { data: reservationSearchReservations, isFetching: isLoadingReservationsAll } = useRepositoryQuery(
        'reservations',
        'getAll',
        {
            reservationIds: [...reservationsSearchData.map(({ id }) => id), ...(defaultValues?.reservationIds ?? [])],
            extent: RESERVATION_EXTENT,
        },
        {
            enabled: (!!reservationsSearchData.length || !!defaultValues?.reservationIds?.length) && !!resourcesData,
        }
    );

    const uniqueReservationGroups = useMemo(
        () =>
            pipe(
                [
                    ...(groupSearchReservations?.reservationGroups || []),
                    ...(reservationSearchReservations?.reservationGroups || []),
                ],
                array.uniq<ReservationGroup>(eq.struct({ id: string.Eq })),
                array.sortBy([sortByGroupName])
            ),
        [groupSearchReservations, reservationSearchReservations]
    );

    const uniqueReservations = useMemo(
        () =>
            pipe(
                enrichReservationResult(groupSearchReservations),
                array.concat(enrichReservationResult(reservationSearchReservations)),
                array.uniq<EnrichedReservation>(eq.struct({ id: string.Eq }))
            ),
        [groupSearchReservations, reservationSearchReservations]
    );

    const reservationsGroupedByGroup = useMemo(
        () =>
            pipe(
                uniqueReservations,
                nonEmptyArray.groupBy((reservation) => reservation.groupId)
            ),
        [uniqueReservations]
    );

    const lastCachedGroups = useRef<TreeOption[]>([]);
    const companionExtent =
        (useCurrentEnterpriseAccomodationEnvironment()?.reservations
            ?.companionExtent as CompanionAccommodationExtent) || CompanionAccommodationExtent.All;

    return useMemo(() => {
        const newGroups = uniqueReservationGroups.map(({ name, id }) => ({
            id,
            label: name,
            value: `${GROUP_PREFIX}${id}`,
            options: pipe(
                reservationsGroupedByGroup[id] || [],
                array.sortBy([sortByReservationDate, sortByResourceName(resourcesData?.resources || {})]),
                array.map((reservation) => ({
                    id: reservation.id,
                    label: <ReservationLabel reservation={reservation} />,
                    description: (
                        <Typography textStyle="bodySmall" variant="tertiary" as="div">
                            {`${reservation.number}${SpacedDash}${reservation.customerName}`}
                        </Typography>
                    ),
                    tagProps: getReservationStateTagProps(reservation, companionExtent),
                    value: `${RESERVATION_PREFIX}${reservation.id}`,
                }))
            ),
        }));

        // Cache only if not loading
        if (
            !isLoadingReservationGroupsSearch &&
            !isLoadingReservationsSearch &&
            !isLoadingReservationsAllGroups &&
            !isLoadingReservationsAll
        ) {
            lastCachedGroups.current = newGroups;
        }

        return {
            groupsOptions: lastCachedGroups.current,
            groupToReservationMap: lastCachedGroups.current.reduce<Record<string, Array<string>>>((acc, group) => {
                if ('value' in group && group.value) {
                    acc[group.value] = 'options' in group ? group.options.map((option) => option.value) : [];
                }
                return acc;
            }, {}),
        };
    }, [
        uniqueReservationGroups,
        reservationsGroupedByGroup,
        resourcesData,
        isLoadingReservationGroupsSearch,
        isLoadingReservationsSearch,
        isLoadingReservationsAllGroups,
        isLoadingReservationsAll,
        companionExtent,
    ]);
};

const enrichReservationResult = (reservationResult?: ReservationResult): EnrichedReservation[] => {
    const accounts: Record<string, Customer> = keyById(reservationResult?.accounts ?? []);

    return (reservationResult?.reservations ?? []).map((reservation) => ({
        ...reservation,
        customerName: reservation.customerId
            ? compactArray([
                  accounts[reservation.customerId].lastName,
                  accounts[reservation.customerId].firstName,
              ]).join(UnicodeEntity.Space)
            : '',
    }));
};

const sortByGroupName = pipe(
    string.Ord,
    ord.contramap((g: ReservationGroup) => g.name)
);

const sortByReservationDate = pipe(
    ord.reverse(string.Ord),
    ord.contramap((r: Reservation) => r.startUtc)
);

const sortByResourceName = (resources: Record<string, Resource>) =>
    pipe(
        string.Ord,
        ord.contramap((r: Reservation) => (r.assignedResourceId ? resources[r.assignedResourceId]?.name : ''))
    );

const useCurrentEnterpriseAccomodationEnvironment = () => {
    const { accommodationEnvironmentCode } = useCurrentEnterprise();
    const { data } = useRepositoryQuery('globalization', 'get', {});
    return data?.accommodationEnvironments?.find(({ code }) => code === accommodationEnvironmentCode);
};

const getReservationStateTagProps = (
    reservation: EnrichedReservation,
    companionExtent: CompanionAccommodationExtent
): { variant: string; children: React.ReactNode } => {
    const state = ServiceOrderState.getByName(reservation.serviceOrderState);
    const timeState = ReservationTimeState.getByName(reservation.reservationTimeState);
    const reservationStateTextKey = getTextKeyFromServiceOrderState(
        state.name,
        timeState.name,
        companionExtent,
        reservation.options
    );
    const variant = getTagVariantFromReservationTimeState(timeState?.value, {
        companionExtent,
        ...reservation.options,
    });

    return {
        variant,
        children: reservationStateTextKey ? <LocalizedText textKey={reservationStateTextKey} /> : null,
    };
};
